/**
 * WCAG 2.1 contrast math, and an audit of the colours a designer actually picks.
 *
 * The static suite in `__a11y__/contrast.a11y.test.ts` proves the DEFAULT
 * palette is accessible. It cannot say anything about a tenant's custom theme,
 * because those colours live in the database and arrive at runtime — so until
 * now a designer could publish an unreadable primary button and nothing
 * anywhere would object.
 *
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */

/** WCAG AA: normal text. */
export const AA_NORMAL = 4.5;
/** WCAG AA: large text (>= 18pt, or 14pt bold). */
export const AA_LARGE = 3;
/** WCAG AA: UI components and graphical objects. */
export const AA_UI = 3;

/**
 * Parse `#rgb` or `#rrggbb` (with or without the hash). Returns null for
 * anything else, which matters because these values come from a live text
 * input: a designer halfway through typing `#1c` must not produce NaN, and a
 * NaN ratio silently compares false against every threshold.
 */
export function parseHex(hex: string | null | undefined): [number, number, number] | null {
  if (!hex) return null;
  const h = hex.trim().replace(/^#/, '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function srgbToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/** Contrast ratio between two colours, or null if either is unparseable. */
export function contrastRatio(fg: string, bg: string): number | null {
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  if (l1 === null || l2 === null) return null;
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export interface ContrastCheck {
  id: string;
  /** What the designer sees on screen, not what the token is called. */
  label: string;
  foreground: string;
  background: string;
  ratio: number;
  required: number;
  passes: boolean;
  /** What to change, in the designer's terms. */
  hint: string;
}

export interface ThemeColors {
  primary_color?: string;
  accent_color?: string;
  bg_warm?: string;
}

/**
 * Default ink and paper. `text-primary` and white are not editable in the
 * three-colour customiser, so they are the fixed side of every pair that
 * involves them.
 */
const INK = '#1c1917';
const WHITE = '#ffffff';

/**
 * Audit the colours the theme customiser exposes.
 *
 * Only pairs the storefront genuinely renders are checked. It would be easy to
 * generate a large matrix of combinations and report failures for pairs that
 * never appear together — that trains people to ignore the panel, which is
 * worse than not having one.
 *
 * The pairs come from real markup:
 *   - `bg-sage-*` + `text-white`  — the primary button (components/ui/Button)
 *   - `text-sage-700`             — outline button labels and links
 *   - `--color-terra`             — accent fills and accent text
 *   - `--bg-warm`                 — the warm page background body text sits on
 */
export function auditThemeColors(colors: ThemeColors): ContrastCheck[] {
  const checks: ContrastCheck[] = [];

  const add = (
    id: string,
    label: string,
    foreground: string | undefined,
    background: string | undefined,
    required: number,
    hint: string,
  ) => {
    if (!foreground || !background) return;
    const ratio = contrastRatio(foreground, background);
    if (ratio === null) return; // mid-typing or malformed — say nothing
    checks.push({
      id,
      label,
      foreground,
      background,
      ratio,
      required,
      passes: ratio >= required,
      hint,
    });
  };

  add(
    'primary-fill',
    'White text on a primary button',
    WHITE,
    colors.primary_color,
    AA_NORMAL,
    'Darken the primary colour until label text is readable on it.',
  );
  add(
    'primary-text',
    'Primary colour as link and outline-button text',
    colors.primary_color,
    WHITE,
    AA_NORMAL,
    'Darken the primary colour, or reserve it for fills rather than text.',
  );
  add(
    'accent-fill',
    'White text on an accent fill',
    WHITE,
    colors.accent_color,
    AA_NORMAL,
    'Darken the accent colour, or use dark text on it instead.',
  );
  add(
    'accent-text',
    'Accent colour used as text',
    colors.accent_color,
    WHITE,
    AA_NORMAL,
    'Darken the accent colour, or keep it for borders and fills only.',
  );
  add(
    'ink-on-warm',
    'Body text on the warm background',
    INK,
    colors.bg_warm,
    AA_NORMAL,
    'Lighten the warm background so body text stays readable on it.',
  );
  add(
    'primary-on-warm',
    'Primary colour on the warm background',
    colors.primary_color,
    colors.bg_warm,
    AA_LARGE,
    'Headings and large text need 3:1 — darken the primary or lighten the background.',
  );

  return checks;
}

/** Just the failures, which is what a panel should lead with. */
export function contrastFailures(colors: ThemeColors): ContrastCheck[] {
  return auditThemeColors(colors).filter((c) => !c.passes);
}

/** `4.53` → `"4.53:1"`, rounded the way WCAG tooling conventionally reports. */
export function formatRatio(ratio: number): string {
  return `${Math.round(ratio * 100) / 100}:1`;
}
